import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  assets as assetsApi,
  assetAllocations as allocationsApi,
  assetMaintenance as maintenanceApi,
  batch
} from '../../api/endpoints';
import type { BatchOperation } from '../../api/endpoints';
import {
  useMultiLedger, useSearch, usePagination, useNotification, useOptionList, useDateRangeFilter,
  useOpenRecordFromLink, findLedgerRow, RECORD_GONE
} from '../../hooks';
import { formatDate, formatCurrency } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import { missingRequired, requiredError } from '../../utils/requiredFields';
import { calculateCurrentValue } from './depreciation';

import { unitsOf, totalCostOf, currentValueOf, valueAtWriteOff, planWriteOff, isSponsored } from './quantity';
import { getStoredUser } from '../../utils/auth';
import type { CountedAsset } from './quantity';
import AssetSummary from './AssetSummary';
import {
  PageHeader, SubTabs, SearchBar, DateRangeFilter, Pagination, Notification, PromptDialog, Modal, FormLayout, Input, Select, SearchableSelect, MemberPicker, Button, TablePanel, PrintButton
} from '../../components/ui';

const EMPTY_ASSET = {
  name: '', category: '', brand: '', serialNo: '', purchaseDate: '',
  purchaseCost: '', depreciationRate: '', vendor: '', warrantyExpiry: '',
  location: '', department: '', quantity: '1',
  // How it was acquired. A sponsored row carries who gave it instead of a
  // vendor; the two are never both filled, so switching type clears the other.
  acquisitionType: 'PURCHASED' as 'PURCHASED' | 'SPONSORED',
  sponsorName: '', sponsorMemberId: ''
};

const ACQUISITION_OPTIONS = [
  { value: 'PURCHASED', label: 'Purchased by church' },
  { value: 'SPONSORED', label: 'Sponsored / Gifted' }
];

type SourceFilter = 'all' | 'PURCHASED' | 'SPONSORED';


const EMPTY_ALLOCATION = { assetRef: '', date: '', custodian: '', newLocation: '', newDepartment: '', purpose: '', notes: '' };
const EMPTY_MAINTENANCE = { assetRef: '', date: '', vendor: '', cost: '', nextServiceDate: '', problem: '' };

const refId = (label) => String(label).split(' - ')[0].trim();

/** The three sibling resources behind this screen. */
type AssetTab = 'registry' | 'allocation' | 'maintenance' | 'writeoffs';
/** The Summary tab reads all three; the rest of the screen works on one. */
type AssetView = AssetTab | 'summary';

/**
 * A written-off batch is a voided registry row (so it leaves every active
 * count) carrying this mark in its reason — which is what tells it apart from
 * a plain deletion in the Write-off Register (CHM-099).
 */
const WRITE_OFF_MARK = 'Written off: ';
const isWriteOff = (r: { recordId?: string; deleteReason?: string }) =>
  /-D\d+$/.test(String(r.recordId || '')) || String(r.deleteReason || '').startsWith(WRITE_OFF_MARK);
const writeOffReasonOf = (r: { deleteReason?: string }) =>
  String(r.deleteReason || '').replace(WRITE_OFF_MARK, '');

const AssetManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [activeView, setActiveView] = useState<AssetView>('summary');
  const isSummary = activeView === 'summary';
  const activeTab: AssetTab = isSummary ? 'registry' : activeView;
  const [viewMode, setViewMode] = useState('history');
  const [assetForm, setAssetForm] = useState(EMPTY_ASSET);
  const [allocForm, setAllocForm] = useState(EMPTY_ALLOCATION);
  const [maintForm, setMaintForm] = useState(EMPTY_MAINTENANCE);
  const [editingId, setEditingId] = useState(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  const isEditing = editingId !== null;
  const formIsSponsored = assetForm.acquisitionType === 'SPONSORED';
  const isDeletedView = viewMode === 'deleted';
  const { notification, notify, dismiss } = useNotification();

  const ledger = useMultiLedger({
    registry: { resource: assetsApi, label: 'Asset', prefix: 'AST' },
    allocation: { resource: allocationsApi, label: 'Allocation', prefix: 'ALC' },
    maintenance: { resource: maintenanceApi, label: 'Maintenance', prefix: 'MNT' }
  }, { notify });

  const categories = useOptionList('church_ast_cat');
  const locations = useOptionList('church_ast_loc');
  const departments = useOptionList('church_ast_dept');

  const registry = ledger.active('registry');
  const assetByRecordId = useMemo(
    () => registry.reduce((acc, a) => ({ ...acc, [a.recordId]: a }), {}),
    [registry]
  );
  // Allocations and maintenance can outlive their asset; look the name up among
  // written-off rows too, so the log still says what the entry was for.
  const assetNameOf = useCallback((recordId) => {
    const live = assetByRecordId[recordId];
    if (live) return live.name;
    const gone = ledger.voided('registry').find((a) => a.recordId === recordId);
    return gone ? `${gone.name} (${isWriteOff(gone) ? 'written off' : 'deleted'})` : 'Unknown Asset';
  }, [assetByRecordId, ledger]);
  const assetLabels = useMemo(
    () => registry.map((a) => `${a.recordId} - ${a.name} (${a.location})`),
    [registry]
  );

  // Depreciated book value of the whole registry. purchaseCost is per unit, so
  // a batch row contributes its unit value multiplied by how many it covers.
  const totalBookValue = useMemo(
    () => registry.reduce(
      (sum, a) => sum + currentValueOf(a),
      0
    ),
    [registry]
  );

  // Rows and items are different numbers once batches exist: 50 chairs is one
  // record but fifty things to account for. Both are shown, because "how many
  // assets do we have" has two honest answers.
  const totalUnits = useMemo(
    () => registry.reduce((sum, a) => sum + unitsOf(a), 0),
    [registry]
  );

  // What the church paid for the registry, kept apart from what it was given.
  // A gifted piano has a value but cost the church nothing; adding the two
  // together would report money that was never spent. Read the church figure
  // alongside book value and maintenance to judge replace-vs-repair.
  const totalPurchasedCost = useMemo(
    () => registry.reduce(
      (sum, a) => sum + (isSponsored(a) ? 0 : totalCostOf(a)),
      0
    ),
    [registry]
  );
  const totalSponsoredValue = useMemo(
    () => registry.reduce(
      (sum, a) => sum + (isSponsored(a) ? totalCostOf(a) : 0),
      0
    ),
    [registry]
  );
  const totalMaintCost = useMemo(
    () => ledger.active('maintenance')
      .reduce((sum, m) => sum + (parseFloat(String(m.cost)) || 0), 0),
    [ledger]
  );

  const isWriteOffTab = activeTab === 'writeoffs';
  // The Write-off Register is the written-off batches out of the registry's
  // voided rows; it has no active/deleted split of its own.
  const writeOffRows = useMemo(() => ledger.voided('registry').filter(isWriteOff), [ledger]);
  const tabRows = isWriteOffTab
    ? writeOffRows
    : isDeletedView
      ? ledger.voided(activeTab).filter((r) => activeTab !== 'registry' || !isWriteOff(r))
      : ledger.active(activeTab);
  const writeOffTotals = useMemo(() => ({
    units: writeOffRows.reduce((sum, r) => sum + unitsOf(r as CountedAsset), 0),
    value: writeOffRows.reduce((sum, r) => sum + valueAtWriteOff(r as CountedAsset), 0)
  }), [writeOffRows]);
  // The purchased/sponsored chip only means something on the registry; the
  // other tabs have no acquisition type and show everything.
  const dataset = useMemo(
    () => (activeTab === 'registry' && sourceFilter !== 'all')
      ? tabRows.filter((r) => (sourceFilter === 'SPONSORED') === isSponsored(r as CountedAsset))
      : tabRows,
    [tabRows, activeTab, sourceFilter]
  );

  const getRowDate = useCallback((r) => r.date || r.purchaseDate, []);
  const range = useDateRangeFilter(dataset, getRowDate);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['recordId', 'name', 'category', 'serialNo', 'location', 'custodian', 'vendor', 'sponsorName', 'problem']
  );
  const page = usePagination(filtered);

  const resetForms = () => {
    setAssetForm(EMPTY_ASSET);
    setAllocForm(EMPTY_ALLOCATION);
    setMaintForm(EMPTY_MAINTENANCE);
    setEditingId(null);
  };

  const switchTab = (tab) => {
    setActiveView(tab);
    setViewMode('history');
    resetForms();
    page.reset();
    clear();
    range.clear();
    setSourceFilter('all');
  };

  const switchView = (mode) => {
    if (mode !== 'add') resetForms();
    setViewMode(mode);
    page.reset();
  };

  const labelFor = (recordId) => {
    const a = assetByRecordId[recordId];
    return a ? `${a.recordId} - ${a.name} (${a.location})` : recordId || '';
  };

  const startEdit = (item) => {
    if (activeTab === 'registry') {
      setAssetForm({
        name: item.name || '', category: item.category || '', brand: item.brand || '',
        serialNo: item.serialNo || '', purchaseDate: item.purchaseDate || '',
        purchaseCost: item.purchaseCost || '', depreciationRate: item.depreciationRate || '',
        vendor: item.vendor || '', warrantyExpiry: item.warrantyExpiry || '',
        location: item.location || '', department: item.department || '',
        quantity: String(unitsOf(item)),
        acquisitionType: isSponsored(item) ? 'SPONSORED' : 'PURCHASED',
        sponsorName: item.sponsorName || '', sponsorMemberId: item.sponsorMemberId || ''
      });
    } else if (activeTab === 'allocation') {
      setAllocForm({
        assetRef: labelFor(item.assetId), date: item.date || '', custodian: item.custodian || '',
        newLocation: item.newLocation || '', newDepartment: item.newDepartment || '',
        purpose: item.purpose || '', notes: item.notes || ''
      });
    } else {
      setMaintForm({
        assetRef: labelFor(item.assetId), date: item.date || '', vendor: item.vendor || '',
        cost: item.cost || '', nextServiceDate: item.nextServiceDate || '', problem: item.problem || ''
      });
    }
    setEditingId(item.rawId);
    setViewMode('add');
  };

  const existingRecordId = (key) =>
    isEditing
      ? (ledger.active(key).find((r) => r.rawId === editingId) as { recordId?: string } | undefined)?.recordId
      : null;

  const handleAssetSubmit = async (e) => {
    e.preventDefault();
    const category = assetForm.category.trim();
    const location = assetForm.location.trim();
    const department = assetForm.department.trim();
    if (!category || !location) { notify('Category and Location are required.', 'error'); return; }

    // A gift with no giver is not a record anyone can act on later.
    const sponsorName = assetForm.sponsorName.trim();
    if (formIsSponsored && !sponsorName) { notify('Sponsor name is required for a sponsored asset.', 'error'); return; }

    // A batch of half a chair is not a thing, and a quantity of 0 would make the
    // row invisible in every total while still occupying the register.
    const quantity = Number(assetForm.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      notify('Quantity must be a whole number of 1 or more.', 'error');
      return;
    }

    categories.ensure(category);
    locations.ensure(location);
    if (department) departments.ensure(department);

    const payload = {
      ...assetForm,
      category, location, department,
      purchaseCost: assetForm.purchaseCost ? parseFloat(assetForm.purchaseCost) : 0.0,
      depreciationRate: assetForm.depreciationRate ? parseFloat(assetForm.depreciationRate) : 0.0,
      quantity,
      // A serial number identifies one physical object, so it cannot describe a
      // batch. The field is disabled above one unit; this drops anything typed
      // before the quantity was raised.
      serialNo: quantity > 1 ? '' : assetForm.serialNo,
      // A row has a vendor or a sponsor, never both; drop whichever side the
      // type was switched away from so a stale name is not saved.
      vendor: formIsSponsored ? '' : assetForm.vendor,
      sponsorName: formIsSponsored ? sponsorName : '',
      sponsorMemberId: formIsSponsored ? assetForm.sponsorMemberId : '',
      recordId: existingRecordId('registry') || ledger.nextSequence('registry'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('registry', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handleAllocationSubmit = async (e) => {
    e.preventDefault();
    const assetId = refId(allocForm.assetRef);
    const newLocation = allocForm.newLocation.trim();
    const newDepartment = allocForm.newDepartment.trim();
    const missing = missingRequired([[assetId, 'Asset'], [newLocation, 'New Location']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }

    locations.ensure(newLocation);
    if (newDepartment) departments.ensure(newDepartment);

    const { assetRef, ...rest } = allocForm;
    const payload = {
      ...rest,
      assetId, newLocation, newDepartment,
      recordId: existingRecordId('allocation') || ledger.nextSequence('allocation'),
      status: 'ACTIVE' as const
    };

    // A transfer moves the asset in the master registry too — saved together
    // (or sent for approval together).
    const target = assetByRecordId[assetId];
    const { rawId: _r, __key: _k, recordType: _t, ...asset } = target || {};
    const moveAsset: BatchOperation[] = target
      ? [batch.change(assetsApi, target.rawId, { ...asset, id: target.rawId, location: newLocation, department: newDepartment })]
      : [];
    if (await ledger.save('allocation', payload, editingId,
      { operations: moveAsset, summary: `Transfer ${assetId} to ${newLocation}` })) {
      resetForms();
      setViewMode('history');
    }
  };

  const handleMaintenanceSubmit = async (e) => {
    e.preventDefault();
    const assetId = refId(maintForm.assetRef);
    if (!assetId) { notify('Asset selection is required.', 'error'); return; }

    const { assetRef, ...rest } = maintForm;
    const payload = {
      ...rest,
      assetId,
      cost: maintForm.cost ? parseFloat(maintForm.cost) : 0.0,
      recordId: existingRecordId('maintenance') || ledger.nextSequence('maintenance'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('maintenance', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  // --- Write-off -----------------------------------------------------------
  // Five broken chairs out of fifty is not a deletion: the row must drop to 45
  // and the five must stay on record with a reason. Rather than a new table,
  // the disposed units are split off as their own VOID row — which the existing
  // Disposed Logs tab already lists, with its reason and date columns.
  const [viewingMaint, setViewingMaint] = useState(null);
  const [writeOff, setWriteOff] = useState(null);
  const [writeOffQty, setWriteOffQty] = useState('1');
  const [writeOffReason, setWriteOffReason] = useState('');

  // "Open the record" from the Approvals inbox. A maintenance log opens in its
  // View; an asset or allocation has no view of its own, so its edit form opens.
  // A deleted (or written-off) one is shown, found by its number, in its list.
  const [linkRow, setLinkRow] = useState(null);
  useOpenRecordFromLink(({ id, ref }) => {
    const row = findLedgerRow(ledger.rows, id, ref) as (typeof ledger.rows)[number] & { status?: string; recordId?: string } | null;
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const key = row.__key as AssetTab;
    if (row.status === 'VOID') {
      switchTab(key === 'registry' && isWriteOff(row) ? 'writeoffs' : key);
      if (!(key === 'registry' && isWriteOff(row))) setViewMode('deleted');
      setSearchTerm(row.recordId || '');
      return;
    }
    switchTab(key);
    setLinkRow(row);
  }, !ledger.loading);
  // startEdit reads the tab it is on: wait until the switch has rendered.
  useEffect(() => {
    if (!linkRow || activeTab !== linkRow.__key) return;
    if (linkRow.__key === 'maintenance') setViewingMaint(linkRow); else startEdit(linkRow);
    setLinkRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkRow, activeTab]);
  const [writingOff, setWritingOff] = useState(false);

  const openWriteOff = (row) => {
    setWriteOff(row);
    setWriteOffQty('1');
    setWriteOffReason('');
  };
  const closeWriteOff = () => setWriteOff(null);

  const submitWriteOff = async () => {
    if (!writeOff) return;
    const owned = unitsOf(writeOff);
    const reason = writeOffReason.trim();
    const split = planWriteOff(writeOff, writeOffQty);

    if (!split) {
      notify(`Enter a whole number between 1 and ${owned}.`, 'error'); return;
    }
    if (!reason) { notify('A reason is required to write off units.', 'error'); return; }
    const count = split.disposed;

    setWritingOff(true);
    try {
      const stamp = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const { rawId, __key, recordType, id, ...fields } = writeOff;

      // The whole batch going at once is an ordinary void, with no split.
      if (split.whole) {
        await assetsApi.update(rawId, {
          ...fields, id: rawId, quantity: owned,
          status: 'VOID', deleteReason: `${WRITE_OFF_MARK}${reason}`, deletedAt: stamp
        });
      } else {
        // Disposals are numbered in order for the asset — AST-1042-D1, then
        // -D2 — so the log reads as a history rather than as a code.
        const priorDisposals = ledger.voided('registry')
          .filter((v) => String(v.recordId || '').startsWith(`${writeOff.recordId}-D`)).length;

        // The disposal row and the reduced quantity are saved together (or sent
        // for approval together), so units are never lost or counted twice.
        // The new row is stamped with whoever wrote it off, not the original creator.
        await batch.run([batch.add(assetsApi, {
          ...fields,
          recordId: `${writeOff.recordId}-D${priorDisposals + 1}`,
          quantity: count,
          serialNo: '',
          createdBy: (getStoredUser()?.name || '').trim() || fields.createdBy,
          status: 'VOID', deleteReason: `${WRITE_OFF_MARK}${reason}`, deletedAt: stamp
        }), batch.change(assetsApi, rawId, { ...fields, id: rawId, quantity: split.remaining })],
        `Write off ${count} of ${owned} ${writeOff.name || writeOff.recordId}`);
      }
      await ledger.refetch();
      notify(`${count} unit${count === 1 ? '' : 's'} written off. Recorded in the Write-off Register.`, 'success');
      closeWriteOff();
    } catch (err) {
      notify((err as Error)?.message || 'Could not write off these units.', 'error');
    } finally {
      setWritingOff(false);
    }
  };

  const sub = { fontSize: '12px', color: t.textFaint };
  const idColumn = { key: 'recordId', header: 'Record ID', nowrap: true, render: (r) => <strong>{r.recordId}</strong> };
  const assetNameCell = (r) => <strong>{assetNameOf(r.assetId)}</strong>;

  const columnsByTab = {
    registry: [
      idColumn,
      {
        key: 'name',
        header: 'Asset Info',
        render: (r) => (<><strong>{r.name}</strong><br /><span style={sub}>{r.category}</span></>)
      },
      { key: 'purchaseDate', header: 'Acquired On', nowrap: true, render: (r) => formatDate(r.purchaseDate) },
      {
        key: 'quantity',
        header: 'Qty',
        align: 'center' as const,
        nowrap: true,
        render: (r) => {
          const units = unitsOf(r);
          return units > 1
            ? <strong style={{ color: t.info }}>{units}</strong>
            : <span style={sub}>1</span>;
        }
      },
      {
        key: 'purchaseCost',
        header: 'Cost / Value',
        align: 'right' as const,
        nowrap: true,
        // Cost is stored per unit; a batch also shows what the batch cost, since
        // that is the figure the total column is built from. On a gift the
        // figure is an estimate, and is marked so it is not read as spend.
        render: (r) => {
          const units = unitsOf(r);
          const unit = parseFloat(String(r.purchaseCost)) || 0;
          return (
            <>
              <div style={{ fontWeight: 'bold' }}>{formatCurrency(unit * units)}</div>
              {units > 1 && <div style={sub}>{formatCurrency(unit)} each</div>}
              {isSponsored(r) && <div style={{ ...sub, color: t.warning }}>est. value (gift)</div>}
            </>
          );
        }
      },
      {
        key: 'currentValue',
        header: 'Current Value',
        align: 'right' as const,
        nowrap: true,
        render: (r) => {
          const units = unitsOf(r);
          const dep = calculateCurrentValue(r.purchaseCost, r.purchaseDate, r.depreciationRate);
          return (
            <>
              <div style={{ fontWeight: 'bold', color: t.income }}>
                {formatCurrency(dep.current * units)}
              </div>
              {r.depreciationRate ? (
                <div style={{ fontSize: '11px', color: t.danger }}>
                  (-{formatCurrency(dep.depreciated * units)} at {r.depreciationRate}%)
                </div>
              ) : null}
            </>
          );
        }
      },
      {
        key: 'location',
        header: 'Location & Dept',
        render: (r) => (<><strong>{r.location}</strong><br /><span style={sub}>{r.department}</span></>)
      },
      {
        key: 'source',
        header: 'Source',
        // Where it came from: the shop it was bought at, or the person who gave it.
        render: (r) => isSponsored(r)
          ? (
            <>
              <strong style={{ color: t.warning }}>🎁 {r.sponsorName || 'Sponsor not recorded'}</strong>
              <br /><span style={sub}>{r.sponsorMemberId ? `Member ${r.sponsorMemberId}` : 'Sponsored'}</span>
            </>
          )
          : (<><strong>{r.vendor || '-'}</strong><br /><span style={sub}>Purchased</span></>)
      }
    ],
    allocation: [
      idColumn,
      { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
      { key: 'asset', header: 'Asset Info', render: assetNameCell },
      {
        key: 'newLocation',
        header: 'New Location',
        render: (r) => (<><strong>{r.newLocation}</strong><br /><span style={sub}>{r.newDepartment}</span></>)
      },
      { key: 'custodian', header: 'Custodian' },
      // Purpose and notes were captured on the allocation form but never shown.
      {
        key: 'notes',
        header: 'Purpose / Notes',
        wrap: true,
        render: (r) => (r.purpose || r.notes)
          ? <>{r.purpose && <strong>{r.purpose}</strong>}{r.purpose && r.notes && <br />}{r.notes}</>
          : '-'
      }
    ],
    maintenance: [
      idColumn,
      { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
      { key: 'asset', header: 'Asset Info', render: assetNameCell },
      {
        key: 'vendor',
        header: 'Vendor & Fix',
        // The problem text can run to paragraphs; keep the row to one line and
        // open the full record from the View action.
        render: (r) => (
          <div style={{ minWidth: 0, maxWidth: '320px' }}>
            <strong>{r.vendor}</strong>
            {r.problem && (
              <div
                title={r.problem}
                style={{ ...sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {r.problem}
              </div>
            )}
          </div>
        )
      },
      {
        key: 'cost',
        header: 'Cost',
        align: 'right' as const,
        nowrap: true,
        render: (r) => <span style={{ fontWeight: 'bold', color: t.expense }}>{formatCurrency(r.cost)}</span>
      }
    ]
  };

  // What each tab puts on paper. Each tab stores different columns, so each
  // prints its own document; the values are the ones the table shows — asset
  // names rather than bare ids, formatted dates and money.
  const PRINT_BY_TAB: Record<AssetTab, { documentType: string; title: string }> = {
    registry: { documentType: 'ASSET_REGISTER', title: 'Asset Register' },
    writeoffs: { documentType: 'ASSET_REGISTER', title: 'Write-off Register' },
    allocation: { documentType: 'ASSET_ALLOCATION_REGISTER', title: 'Asset Allocation Register' },
    maintenance: { documentType: 'ASSET_MAINTENANCE_REGISTER', title: 'Asset Maintenance Log' }
  };

  const printRowOf = (r) => {
    if (activeTab === 'allocation') {
      return {
        ...r,
        date: formatDate(r.date),
        assetName: assetNameOf(r.assetId),
        purpose: [r.purpose, r.notes].filter(Boolean).join(' — ')
      };
    }
    if (activeTab === 'maintenance') {
      return {
        ...r,
        date: formatDate(r.date),
        nextServiceDate: formatDate(r.nextServiceDate),
        cost: formatCurrency(r.cost),
        assetName: assetNameOf(r.assetId)
      };
    }
    const sponsored = isSponsored(r as CountedAsset);
    return {
      ...r,
      quantity: unitsOf(r as CountedAsset),
      purchaseDate: formatDate(r.purchaseDate),
      warrantyExpiry: formatDate(r.warrantyExpiry),
      purchaseCost: formatCurrency(r.purchaseCost),
      totalCost: formatCurrency(totalCostOf(r as CountedAsset)),
      currentValue: formatCurrency(isWriteOffTab ? valueAtWriteOff(r as CountedAsset) : currentValueOf(r as CountedAsset)),
      acquisition: sponsored ? 'Sponsored' : 'Purchased',
      source: (sponsored ? r.sponsorName : r.vendor) || '-',
      deleteReason: isWriteOffTab ? writeOffReasonOf(r) : r.deleteReason
    };
  };

  const viewMaintButton = (r) => (
    <Button variant="ghost" size="sm" title="View full record" onClick={() => setViewingMaint(r)}>👁️ View</Button>
  );

  // One line of the allocation or maintenance log on its own sheet — a transfer
  // slip for the receiver, or a service record for the file.
  const printRecordButton = (r) => (activeTab === 'allocation' || activeTab === 'maintenance') && (
    <PrintButton
      module="Asset Management"
      documentType={activeTab === 'allocation' ? 'ASSET_TRANSFER_RECORD' : 'ASSET_MAINTENANCE_RECORD'}
      size="sm"
      variant="ghost"
      label="🖨️ Print"
      title={`${activeTab === 'allocation' ? 'Asset-Transfer' : 'Asset-Maintenance'}-${r.recordId || r.rawId}`}
      data={() => printRowOf(r)}
      onNotify={notify}
      isDarkMode={isDarkMode}
    />
  );

  const tailColumns = isDeletedView
    ? [
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={sub}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
        ...((activeTab === 'maintenance' || activeTab === 'allocation')
          ? [{
              key: 'actions',
              header: 'Actions',
              align: 'right' as const,
              render: (r) => (
                <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                  {activeTab === 'maintenance' && viewMaintButton(r)}
                  {printRecordButton(r)}
                </div>
              )
            }]
          : [])
      ]
    : [
        {
          key: 'actions',
          header: 'Actions',
          align: 'right' as const,
          render: (r) => (
            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
              {activeTab === 'maintenance' && viewMaintButton(r)}
              {printRecordButton(r)}
              <Button write variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
              {/* A batch can lose part of itself; a single item is written off whole. */}
              {activeTab === 'registry' && (
                <Button write variant="ghost" size="sm" onClick={() => openWriteOff(r)}>Write Off</Button>
              )}
              <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => ledger.requestVoid(r)}>Delete</Button>
            </div>
          )
        }
      ];

  const writeOffColumns = [
    { key: 'deletedAt', header: 'Written Off On', nowrap: true, render: (r) => <strong>{r.deletedAt || '-'}</strong> },
    {
      key: 'asset',
      header: 'Asset',
      render: (r) => (
        <>
          <strong>{r.name}</strong>
          <br /><span style={sub}>{String(r.recordId || '').replace(/-D\d+$/, '')} • {r.category}{r.serialNo ? ` • ${r.serialNo}` : ''}</span>
        </>
      )
    },
    { key: 'quantity', header: 'Units', align: 'right' as const, nowrap: true, render: (r) => <strong>{unitsOf(r as CountedAsset)}</strong> },
    {
      key: 'value',
      header: 'Book Value Lost',
      align: 'right' as const,
      nowrap: true,
      render: (r) => <span style={{ color: t.expense, fontWeight: 'bold' }}>{formatCurrency(valueAtWriteOff(r as CountedAsset))}</span>
    },
    { key: 'location', header: 'Was At', render: (r) => (<>{r.location || '-'}<br /><span style={sub}>{r.department || ''}</span></>) },
    { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{writeOffReasonOf(r) || '-'}</span> }
  ];

  // The register lists voided rows, so the deleted-view tail (deleted on / reason) is not repeated.
  const columns = isWriteOffTab
    ? [...writeOffColumns, { key: 'createdBy', header: 'Recorded By', render: (r) => (r as { createdBy?: string }).createdBy || '-' }]
    : [...columnsByTab[activeTab], { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' }, ...tailColumns];

  // Locked while editing: re-pointing a saved allocation/maintenance log at a
  // different asset would silently move that asset's history.
  const assetPicker = (value, onChange) => (
    <SearchableSelect
      label="Select Asset (From Registry)" required fullWidth
      allowAdd={false} allowManage={false}
      disabled={isEditing}
      placeholder="Search active assets..."
      value={value}
      onChange={onChange}
      options={assetLabels}
    />
  );

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="🗄️"
        title="Asset Management"
        activeTab={activeView}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'registry', label: '🗄️ Master Registry' },
          { key: 'allocation', label: '🔄 Allocation & Transfer' },
          { key: 'maintenance', label: '🛠️ Maintenance Log' },
          { key: 'writeoffs', label: `📉 Write-off Register (${writeOffRows.length})` }
        ]}
      />

      {isSummary && (
        <AssetSummary
          assets={registry}
          allocations={ledger.active('allocation')}
          maintenance={ledger.active('maintenance')}
          loading={ledger.loading}
          isDarkMode={isDarkMode}
        />
      )}

      {isWriteOffTab && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: t.spacing.md, marginBottom: t.spacing.lg
        }}>
          {[
            { label: 'Units Written Off', value: writeOffTotals.units.toLocaleString('en-IN'), accent: t.danger },
            { label: 'Book Value Written Off', value: formatCurrency(writeOffTotals.value), accent: t.expense },
            { label: 'Write-off Entries', value: String(writeOffRows.length), accent: t.warning }
          ].map((kpi) => (
            <div key={kpi.label} style={{
              backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`,
              borderLeft: `4px solid ${kpi.accent}`, borderRadius: t.radius.lg, padding: t.spacing.md
            }}>
              <div style={{ fontSize: '12px', color: kpi.accent, fontWeight: 'bold', textTransform: 'uppercase' }}>{kpi.label}</div>
              <div style={{ fontSize: '22px', fontWeight: 900, color: t.textMain }}>{kpi.value}</div>
            </div>
          ))}
          <p style={{ gridColumn: '1 / -1', margin: 0, color: t.textMuted, fontSize: t.fontSize.md }}>
            Every batch written off from the Master Registry, with the units, their book value at the time, and the
            reason given. Use <strong>Write Off</strong> on a registry row to add to it.
          </p>
        </div>
      )}

      {!isSummary && activeTab === 'registry' && !isDeletedView && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: t.spacing.md, marginBottom: t.spacing.lg
        }}>
          {[
            { label: 'Items Owned', value: totalUnits, accent: t.warning, count: true },
            { label: 'Church Investment', value: totalPurchasedCost, accent: t.info },
            { label: 'Sponsored Value', value: totalSponsoredValue, accent: t.warning },
            { label: 'Current Book Value', value: totalBookValue, accent: t.income },
            { label: 'Total Maintenance Spent', value: totalMaintCost, accent: t.expense }
          ].map((kpi) => (
            <div key={kpi.label} style={{
              backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`,
              borderLeft: `4px solid ${kpi.accent}`, borderRadius: t.radius.lg, padding: t.spacing.md
            }}>
              <div style={{ fontSize: '12px', color: kpi.accent, fontWeight: 'bold', textTransform: 'uppercase' }}>
                {kpi.label}
              </div>
              <div style={{ fontSize: '22px', fontWeight: 900, color: t.textMain }}>
                {kpi.count ? kpi.value.toLocaleString('en-IN') : formatCurrency(kpi.value)}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isSummary && !isWriteOffTab && (
        <SubTabs
          value={viewMode}
          onChange={switchView}
          tabs={[
            { key: 'history', label: '📋 View Active Records' },
            { key: 'deleted', label: '🗑️ Deleted', variant: 'danger' },
            { key: 'add', write: true, label: isEditing ? '✎ Editing Record...' : '+ Add New Entry' }
          ]}
        />
      )}

      {!isSummary && (viewMode === 'history' || isDeletedView) && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by Name, Category, Serial, Location or ID..."
            actions={
              <>
                <PrintButton
                  module="Asset Management"
                  documentType={PRINT_BY_TAB[activeTab].documentType}
                  size="sm"
                  title={PRINT_BY_TAB[activeTab].title.replace(/ /g, '-')}
                  data={() => ({
                    title: isDeletedView ? `${PRINT_BY_TAB[activeTab].title} (Deleted)` : PRINT_BY_TAB[activeTab].title,
                    reference: [
                      searchTerm && `Search: "${searchTerm}"`,
                      activeTab === 'registry' && sourceFilter !== 'all' && (sourceFilter === 'SPONSORED' ? 'Sponsored only' : 'Purchased only'),
                      `Printed ${formatDate(new Date().toISOString())}`
                    ].filter(Boolean).join(' · '),
                    // Every line that matches the filters, not just the page on screen.
                    rowCount: filtered.length,
                    rows: filtered.map(printRowOf)
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
                {activeTab === 'registry' && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {([
                      { key: 'all', label: 'All' },
                      { key: 'PURCHASED', label: 'Purchased' },
                      { key: 'SPONSORED', label: '🎁 Sponsored' }
                    ] as { key: SourceFilter; label: string }[]).map((chip) => (
                      <Button
                        key={chip.key} size="sm"
                        variant={sourceFilter === chip.key ? 'primary' : 'ghost'}
                        onClick={() => { setSourceFilter(chip.key); page.reset(); }}
                      >
                        {chip.label}
                      </Button>
                    ))}
                  </div>
                )}
                {!isWriteOffTab && <DateRangeFilter {...range} onChange={page.reset} />}
              </>
            }
          />

          <TablePanel
            title={<>Asset Management — {activeTab === 'registry' ? 'Master Registry' : activeTab === 'allocation' ? 'Allocation & Transfer' : activeTab === 'writeoffs' ? 'Write-off Register' : 'Maintenance Log'}</>}
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
              columns={columns}
              rows={page.currentItems}
              rowKey="rawId"
              loading={ledger.loading}
              emptyMessage="No records found for this category."
          />
        </>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'registry' && (
        <FormLayout
          title={isEditing ? 'Edit Asset' : 'Register New Asset'}
          submitLabel={isEditing ? 'Update Asset' : 'Save Asset'}
          submitting={ledger.saving}
          onSubmit={handleAssetSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <Select
              label="Acquired By" required placeholder=""
              hint={formIsSponsored
                ? 'A gift — record who gave it. The church spent nothing.'
                : 'Bought with church money.'}
              value={assetForm.acquisitionType}
              onChange={(e) => setAssetForm((prev) => ({
                ...prev, acquisitionType: e.target.value as 'PURCHASED' | 'SPONSORED'
              }))}
              options={ACQUISITION_OPTIONS}
              style={{ fontWeight: 'bold' }}
            />
            <Input
              label="Asset Name" required placeholder="e.g. Yamaha PSR-E473"
              value={assetForm.name}
              onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
              style={{ fontWeight: 'bold' }}
            />
            <SearchableSelect
              label="Asset Category" required placeholder="Search or type category..."
              value={assetForm.category}
              onChange={(v) => setAssetForm((prev) => ({ ...prev, category: v }))}
              options={categories.options} onOptionsChange={categories.setOptions}
            />
            <Input
              label="Brand / Make" placeholder="e.g. Yamaha"
              value={assetForm.brand}
              onChange={(e) => setAssetForm({ ...assetForm, brand: e.target.value })}
            />
            <Input
              label="Quantity" type="number" min="1" step="1" required
              hint="How many identical units this record covers. Use 1 for a single item."
              value={assetForm.quantity}
              onChange={(e) => setAssetForm({ ...assetForm, quantity: e.target.value })}
              style={{ fontWeight: 'bold' }}
            />
            <Input
              label="Model / Serial No" placeholder="e.g. SN-99213"
              // A serial number names one object, so it cannot describe a batch.
              disabled={unitsOf(assetForm) > 1}
              hint={unitsOf(assetForm) > 1
                ? 'Not available for a batch — a serial number identifies a single item.'
                : undefined}
              value={unitsOf(assetForm) > 1 ? '' : assetForm.serialNo}
              onChange={(e) => setAssetForm({ ...assetForm, serialNo: e.target.value })}
            />
            <Input
              label={formIsSponsored ? 'Date Received' : 'Purchase Date'} type="date"
              value={assetForm.purchaseDate}
              onChange={(e) => setAssetForm({ ...assetForm, purchaseDate: e.target.value })}
            />
            <Input
              label={formIsSponsored ? 'Estimated Value per Unit (₹)' : 'Purchase Cost per Unit (₹)'}
              type="number" step="0.01" placeholder="0.00"
              // Cost is per unit, so on a batch the number typed here is not the
              // number that lands in the register. Show the multiplication while
              // it is still being typed, rather than after saving.
              hint={unitsOf(assetForm) > 1
                ? `${unitsOf(assetForm)} x ${formatCurrency(parseFloat(assetForm.purchaseCost) || 0)} = ${formatCurrency(totalCostOf(assetForm))} total`
                : formIsSponsored
                  ? 'For valuation and depreciation only — not money the church spent.'
                  : 'Cost of a single unit.'}
              value={assetForm.purchaseCost}
              onChange={(e) => setAssetForm({ ...assetForm, purchaseCost: e.target.value })}
              style={{ fontWeight: 'bold' }}
            />
            <Input
              label="Annual Dep. %" type="number" step="0.01" placeholder="e.g. 10"
              hint="Straight-line, applied per completed year since purchase."
              value={assetForm.depreciationRate}
              onChange={(e) => setAssetForm({ ...assetForm, depreciationRate: e.target.value })}
            />
            {formIsSponsored ? (
              <>
                {/* Pick from the rolls when the giver is a member, so the gift is
                    linked to their record; an outsider is recorded by name alone. */}
                <MemberPicker
                  label="Sponsor (Church Member)"
                  hint="Leave blank if the sponsor is not a church member."
                  placeholder="Search members..."
                  isDarkMode={isDarkMode}
                  value={assetForm.sponsorMemberId}
                  onChange={(memberId, member) => setAssetForm((prev) => ({
                    ...prev,
                    sponsorMemberId: memberId,
                    sponsorName: member?.name || prev.sponsorName
                  }))}
                />
                <Input
                  label="Sponsor Name" required placeholder="Who gave this asset"
                  hint="Filled in when a member is picked; type it for anyone else."
                  value={assetForm.sponsorName}
                  onChange={(e) => setAssetForm({ ...assetForm, sponsorName: e.target.value })}
                  style={{ fontWeight: 'bold' }}
                />
              </>
            ) : (
              <Input
                label="Vendor / Supplier" placeholder="Supplier name"
                value={assetForm.vendor}
                onChange={(e) => setAssetForm({ ...assetForm, vendor: e.target.value })}
              />
            )}
            <Input
              label="Warranty Expiry Date" type="date"
              value={assetForm.warrantyExpiry}
              onChange={(e) => setAssetForm({ ...assetForm, warrantyExpiry: e.target.value })}
            />
            <SearchableSelect
              label="Initial Location" required placeholder="Search or type location..."
              value={assetForm.location}
              onChange={(v) => setAssetForm((prev) => ({ ...prev, location: v }))}
              options={locations.options} onOptionsChange={locations.setOptions}
            />
            <SearchableSelect
              label="Department (Optional)" placeholder="Search or type department..."
              value={assetForm.department}
              onChange={(v) => setAssetForm((prev) => ({ ...prev, department: v }))}
              options={departments.options} onOptionsChange={departments.setOptions}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'allocation' && (
        <FormLayout
          title={isEditing ? 'Edit Transfer' : 'Allocate / Transfer Asset'}
          submitLabel={isEditing ? 'Update Transfer' : 'Process Transfer'}
          submitting={ledger.saving}
          onSubmit={handleAllocationSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            {assetPicker(allocForm.assetRef, (v) => setAllocForm((prev) => ({ ...prev, assetRef: v })))}
            <Input
              label="Transfer Date" type="date" required
              value={allocForm.date}
              onChange={(e) => setAllocForm({ ...allocForm, date: e.target.value })}
            />
            <Input
              label="Assigned Custodian / Receiver" required placeholder="Name of receiver"
              value={allocForm.custodian}
              onChange={(e) => setAllocForm({ ...allocForm, custodian: e.target.value })}
            />
            <SearchableSelect
              label="New Location" required placeholder="Search or type location..."
              value={allocForm.newLocation}
              onChange={(v) => setAllocForm((prev) => ({ ...prev, newLocation: v }))}
              options={locations.options} onOptionsChange={locations.setOptions}
            />
            <SearchableSelect
              label="New Department" placeholder="Search or type department..."
              value={allocForm.newDepartment}
              onChange={(v) => setAllocForm((prev) => ({ ...prev, newDepartment: v }))}
              options={departments.options} onOptionsChange={departments.setOptions}
            />
            <Input
              label="Purpose / Notes" type="textarea" rows={2} fullWidth
              placeholder="Reason for the transfer..."
              value={allocForm.purpose}
              onChange={(e) => setAllocForm({ ...allocForm, purpose: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'maintenance' && (
        <FormLayout
          title={isEditing ? 'Edit Maintenance Log' : 'Log Maintenance / Repair'}
          submitLabel={isEditing ? 'Update Log' : 'Save Maintenance Log'}
          submitting={ledger.saving}
          onSubmit={handleMaintenanceSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            {assetPicker(maintForm.assetRef, (v) => setMaintForm((prev) => ({ ...prev, assetRef: v })))}
            <Input
              label="Service Date" type="date" required
              value={maintForm.date}
              onChange={(e) => setMaintForm({ ...maintForm, date: e.target.value })}
            />
            <Input
              label="Service Vendor / Technician" required placeholder="Who carried out the work"
              value={maintForm.vendor}
              onChange={(e) => setMaintForm({ ...maintForm, vendor: e.target.value })}
            />
            <Input
              label="Repair Cost (₹)" type="number" step="0.01" placeholder="0.00"
              value={maintForm.cost}
              onChange={(e) => setMaintForm({ ...maintForm, cost: e.target.value })}
              style={{ color: t.expense, fontWeight: 'bold' }}
            />
            <Input
              label="Next Service Due Date" type="date"
              value={maintForm.nextServiceDate}
              onChange={(e) => setMaintForm({ ...maintForm, nextServiceDate: e.target.value })}
            />
            <Input
              label="Problem & Solution Details" type="textarea" rows={2} fullWidth
              placeholder="What was wrong and what was done..."
              value={maintForm.problem}
              onChange={(e) => setMaintForm({ ...maintForm, problem: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      <PromptDialog
        open={Boolean(ledger.voiding)}
        title={`Delete ${ledger.voiding?.recordType || ''} ${ledger.voiding?.recordId || ''}`}
        message="Please enter the reason for removing this record. It will be kept in Deleted History."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={(reason) => ledger.confirmVoid(reason)}
        onCancel={ledger.cancelVoid}
      />

      {viewingMaint && (
        <Modal
          open
          title={`Maintenance Log — ${viewingMaint.recordId || ''}`}
          onClose={() => setViewingMaint(null)}
          isDarkMode={isDarkMode}
          width="560px"
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: t.fontSize.md }}>
            <tbody>
              {([
                ['Record ID', viewingMaint.recordId],
                ['Asset', assetNameOf(viewingMaint.assetId)],
                ['Service Date', viewingMaint.date ? formatDate(viewingMaint.date) : ''],
                ['Vendor / Technician', viewingMaint.vendor],
                ['Repair Cost', formatCurrency(viewingMaint.cost)],
                ['Next Service Due', viewingMaint.nextServiceDate ? formatDate(viewingMaint.nextServiceDate) : ''],
                ['Problem & Solution', viewingMaint.problem],
                ['Created By', viewingMaint.createdBy],
                ...(viewingMaint.deleteReason ? [['Delete Reason', viewingMaint.deleteReason]] : [])
              ] as Array<[string, string | undefined]>).map(([label, value]) => (
                // Colours come from the theme: the modal panel sets only a
                // background, so unstyled text would stay dark in dark mode.
                <tr key={label} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: '7px 10px', color: t.textMuted, width: '170px', verticalAlign: 'top' }}>{label}</td>
                  <td style={{
                    padding: '7px 10px', fontWeight: 'bold', verticalAlign: 'top',
                    color: label === 'Repair Cost' ? t.expense : label === 'Delete Reason' ? t.danger : t.textMain,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                  }}>
                    {value || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}

      {writeOff && (
        <Modal
          open
          title={`Write Off — ${writeOff.name}`}
          onClose={closeWriteOff}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={closeWriteOff}>Cancel</Button>
              <Button variant="danger" disabled={writingOff} onClick={submitWriteOff}>
                {writingOff ? 'Writing off…' : 'Write Off Units'}
              </Button>
            </div>
          }
        >
          <p style={{ marginTop: 0, color: t.textMuted }}>
            {unitsOf(writeOff) === 1
              ? 'This item leaves the registry and is kept in the Write-off Register with this reason.'
              : `${unitsOf(writeOff)} in the register. The units you write off are kept in the Write-off Register with this reason; the rest stay in service.`}
          </p>
          <Input
            label="Units to write off" type="number" min="1" max={String(unitsOf(writeOff))} step="1" required
            value={writeOffQty}
            onChange={(e) => setWriteOffQty(e.target.value)}
          />
          <Input
            label="Reason" required placeholder="e.g. Broken beyond repair after the storm"
            value={writeOffReason}
            onChange={(e) => setWriteOffReason(e.target.value)}
          />
        </Modal>
      )}
    </div>
  );
};

export default AssetManagement;
